import React, { useState, useEffect, useRef } from 'react';
import { Mail, Check, Loader2 } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { useAnonFollows } from '../hooks/useAnonFollows';
import EmailSubscribeForm from './EmailSubscribeForm';

/**
 * EmailAlertButton -- email subscription button for all users
 *
 * Signed-in users: auto-subscribes using their Clerk email on click (no popover).
 * Anonymous users: shows the email input popover on click.
 * Uses the email_subscribers table (server) + localStorage tracking (client).
 *
 * Two variants via `compact` prop:
 * - Full (action rows): outlined button with Mail icon
 * - Compact (list rows): small pill
 */

// Contextual message for the popover
const getAlertMessage = (targetType, label) => {
  switch (targetType) {
    case 'counterpart':
      return label
        ? `Get daily email alerts when new ${label} deals are announced.`
        : 'Get daily email alerts when new deals involving this company are announced.';
    case 'technology':
      return label
        ? `Get daily email alerts when new ${label} deals are announced.`
        : 'Get daily email alerts for deals in this sector.';
    case 'country':
      return label
        ? `Get daily email alerts for new deals in ${label}.`
        : 'Get daily email alerts for deals in this market.';
    default:
      return 'Get daily email alerts.';
  }
};

const EmailAlertButton = ({ targetType, targetId, label, compact = false }) => {
  const { isSignedIn, user } = useUser();
  const { isAnonFollowing, addAnonFollow } = useAnonFollows();
  const [showPopover, setShowPopover] = useState(false);
  const [toast, setToast] = useState(null); // 'subscribed' | null
  const [autoSubLoading, setAutoSubLoading] = useState(false);
  const popoverRef = useRef(null);

  // Auto-dismiss toast after 2s
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Click outside to dismiss popover
  useEffect(() => {
    if (!showPopover) return;
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setShowPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPopover]);

  if (!targetType || !targetId) return null;

  const subscribed = isAnonFollowing(targetType, targetId);

  // Signed-in users: auto-subscribe using their Clerk email (no popover)
  const handleSignedInClick = async (e) => {
    e.stopPropagation();
    if (autoSubLoading || subscribed) return;

    const email = user?.emailAddresses?.[0]?.emailAddress;
    if (!email) return;

    setAutoSubLoading(true);
    try {
      const res = await fetch('/api/email-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), targetType, targetId }),
      });
      if (res.ok) {
        addAnonFollow(targetType, targetId, label);
        setToast('subscribed');
      }
    } catch {
      // Network error — silent fail
    } finally {
      setAutoSubLoading(false);
    }
  };

  // Anonymous users: show email input popover
  const handleClick = (e) => {
    e.stopPropagation();
    if (isSignedIn) {
      handleSignedInClick(e);
    } else {
      setShowPopover(true);
    }
  };

  // Email subscribe popover
  const EmailPopover = ({ alignRight }) => (
    <div
      ref={popoverRef}
      className={`absolute top-full mt-2 w-80 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 p-4 z-50 animate-fade-in ${
        alignRight ? 'right-0' : 'left-1/2 -translate-x-1/2'
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start gap-2.5 mb-3">
        <Mail size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-slate-700 dark:text-slate-300 leading-snug">
          {getAlertMessage(targetType, label)}
        </p>
      </div>
      <EmailSubscribeForm
        targetType={targetType}
        targetId={targetId}
        buttonLabel="Get Daily Email"
        variant="stacked"
        onSuccess={() => {
          addAnonFollow(targetType, targetId, label);
          setToast('subscribed');
          setTimeout(() => setShowPopover(false), 2000);
        }}
      />
      <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-2 text-center">No account needed. Unsubscribe anytime.</p>
      {/* Small arrow pointing up */}
      <div className={`absolute -top-2 w-4 h-4 bg-white dark:bg-slate-800 border-l border-t border-slate-200 dark:border-slate-700 rotate-45 ${
        alignRight ? 'right-3' : 'left-1/2 -translate-x-1/2'
      }`} />
    </div>
  );

  /* -- Compact variant: mini pill -- */
  if (compact) {
    return (
      <span className="relative inline-flex">
        <button
          onClick={handleClick}
          className={`px-2.5 py-1 rounded-lg text-xs font-medium flex items-center gap-1 transition-all ${
            subscribed
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'text-blue-700 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20 dark:hover:bg-blue-900/40'
          }`}
        >
          {autoSubLoading ? <Loader2 size={12} className="animate-spin" /> : toast === 'subscribed' ? <Check size={12} /> : <Mail size={12} />}
          {toast === 'subscribed'
            ? 'Email alerts on!'
            : subscribed
              ? 'Subscribed'
              : 'Get Alerts'}
        </button>
        {showPopover && !isSignedIn && <EmailPopover alignRight />}
      </span>
    );
  }

  /* -- Full variant: outlined button -- */
  return (
    <span className="relative inline-flex">
      <button
        onClick={handleClick}
        className={`px-4 py-2 border rounded-xl text-sm font-medium flex items-center gap-2 transition-all ${
          subscribed
            ? 'border-blue-300 text-blue-700 bg-white hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:bg-transparent dark:hover:bg-blue-900/20'
            : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 shadow-sm'
        }`}
      >
        {autoSubLoading ? <Loader2 size={14} className="animate-spin" /> : toast === 'subscribed' ? <Check size={14} /> : <Mail size={14} />}
        {toast === 'subscribed'
          ? 'Email alerts on!'
          : subscribed
            ? 'Subscribed'
            : 'Get Alerts'}
      </button>
      {showPopover && !isSignedIn && <EmailPopover />}
    </span>
  );
};

export default EmailAlertButton;
